/*
 * vms-gallery-video.js (2026-08-26, owner-directed)
 *
 * Drives two related PDP gallery behaviours. It is loaded by
 * snippets/vms-product-media-gallery.liquid only when at least one of its two
 * theme-editor switches is on (video_autoplay, thumb_video_preview), so a store
 * with neither on never ships this file's work.
 *
 * WHY IT EXISTS. Stock Dawn shows a native product video as a poster with a play
 * button, and the real video only loads after a tap. The owner: "the shopify
 * interface for playing the videos is clunky and you end up with an overlay on
 * the video when trying to play a video and the video is over before the overlay
 * hides. this is especially a problem on mobile." So the video now plays inline.
 *
 * THE HARD PART, AND HOW IT IS SOLVED. Dawn's window.pauseAllMedia() pauses EVERY
 * <video> on the page (main videos AND thumbnail previews) whenever a slide is
 * activated, a variant changes, or the lightbox opens or closes. The first build
 * guessed when that happened from timing and got three things wrong: thumbnail
 * previews never restarted, a lightbox open marked the inline video as a manual
 * pause so it stayed paused after close, and a tab-hide did the same. The fix is
 * an EXACT hook: we wrap window.pauseAllMedia once, so we know precisely when Dawn
 * paused everything, and 60 ms later we re-assert. Timing is now only a fallback
 * for the defensive case where the global is somehow absent.
 *
 * BEHAVIOUR 1, MAIN GALLERY VIDEOS (video[data-vms-autoplay]). Muted, looping,
 * playsinline, native controls. An IntersectionObserver (threshold 0.5, created
 * only after window load) plays a video when its slide is on screen and pauses it
 * when it is not. After each Dawn pause we replay whatever main video is on screen,
 * UNLESS a product-modal is open (the lightbox is covering the page) or the shopper
 * paused that video on purpose.
 *
 * BEHAVIOUR 2, DESKTOP VIDEO THUMBNAILS (video[data-vms-thumb]). Once the page has
 * loaded, every video thumbnail whose rail is on screen plays a muted preview that
 * LOOPS continuously (owner: "the video's should loop. not just play 1 time
 * through"). Because a loop that runs longer than 5 seconds next to other content
 * needs a pause mechanism under WCAG 2.2.2 (Pause, Stop, Hide), the gallery renders
 * one "Pause previews" control under the rail; this script toggles it, pausing or
 * resuming every thumbnail preview, and remembers the choice for the session in
 * sessionStorage (key vmsThumbPreviewPaused, storage guarded with try/catch). After
 * each Dawn pause the previews replay too, unless the shopper paused them. The rail
 * is display:none on phones, so this whole behaviour is skipped there (offsetParent).
 *
 * BEHAVIOUR 3, IN-DESCRIPTION PAGE VIDEOS
 * (video[data-vms-page], plus the legacy video[autoplay] that is not data-vms-autoplay
 * and not data-vms-thumb). The owner authored a muted, looping clip into the 33FD001
 * description, below the copy and above a lazy YouTube embed. Owner: "there is a video
 * below the product description. It should auto play also. The youtube video can remain
 * non-autoplay." Dawn's pauseAllMedia killed it on any gallery action and nothing brought
 * it back, so after a shopper opened a gallery video the description clip sat dead; it is
 * now a managed set, re-asserted like the gallery videos, at threshold 0.25 because a wide
 * description clip is often only partly in view. iframes are never selected, so the lazy
 * YouTube embed is unchanged.
 *
 * [NOTE] The clip used to carry a native autoplay attribute. Per the HTML spec that makes
 * the browser fetch the video at PARSE time, before the load event (about 367 KB on 33FD001
 * on every device), and it was the ONLY pre-load video fetch PSI could see on mobile. So the
 * clip now carries data-vms-page with preload="none" and NO autoplay: nothing fetches until
 * the visibility observer (threshold 0.25, created after window load) first calls play(),
 * exactly like a main gallery video. The old video[autoplay] branch stays selected so any
 * future authored autoplay clip is still managed.
 *
 * [RULE, owner 2026-08-26] PAGE LOAD COMES FIRST. Owner: "the video loading should
 * not slow down the rest of the page loading. Make sure to check the full page renders
 * and then the videos finish loading and autoplay." Three mechanisms enforce this:
 * (1) every <video> is preload="none" with NO autoplay attribute, so nothing fetches
 * until this script calls play(); (2) the main-video IntersectionObserver, the only
 * thing that calls play() on the hero videos, is not created until the window load
 * event has fired (or the next idle tick if the page is already complete), so even a
 * video on screen at first paint cannot begin fetching before the page has rendered;
 * (3) posters are kept small (main 800, thumb 208) so the images that DO load with the
 * page stay light. The thumbnail previews were already gated behind window load plus
 * requestIdleCallback, so they honor the same rule.
 *
 * REDUCED MOTION. Under prefers-reduced-motion: reduce we do nothing: no hook, no
 * observer, no thumbnail playback. The main videos keep their controls, so a shopper
 * can still press play; the thumbnails simply never move (the snippet's CSS hides both
 * the preview layer and the Pause control).
 *
 * No dependencies and no globals beyond an optional window.VMSGalleryVideo used
 * for debugging, plus the one deliberate wrap of window.pauseAllMedia. Every DOM
 * lookup is guarded for absence.
 */
(function () {
  'use strict';

  var REDUCED = false;
  try {
    REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    REDUCED = false;
  }

  // ---- shared state ---------------------------------------------------------
  // dawnPauseAt: when the wrapped window.pauseAllMedia last ran (the exact signal).
  // hiddenAt: when the tab last went hidden. lastActionAt: fallback-only timing, set
  // by the slideChanged / thumbnail-click listeners that are wired only when we could
  // NOT install the hook. resumeCallbacks: each behaviour registers one; afterDawnPause
  // runs them all, so neither behaviour reaches into the other's closure.
  var dawnPauseAt = 0;
  var hiddenAt = 0;
  var lastActionAt = 0;
  var hookInstalled = false;
  var resumeCallbacks = [];

  function safePlay(video) {
    if (!video) return;
    var p;
    try {
      p = video.play();
    } catch (e) {
      return;
    }
    // play() rejects when autoplay is blocked or the element is torn down mid-call.
    // Neither is actionable here, so swallow it rather than logging noise.
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  function anyModalOpen() {
    return !!document.querySelector('product-modal[open]');
  }

  function afterDawnPause() {
    for (var i = 0; i < resumeCallbacks.length; i++) {
      resumeCallbacks[i]();
    }
  }

  function registerResume(cb) {
    resumeCallbacks.push(cb);
  }

  // Wrap Dawn's global pause so we know EXACTLY when Dawn paused everything, instead of
  // guessing from timing. Dawn calls it as window.pauseAllMedia() and as a bare
  // pauseAllMedia(); both read the window property at call time, so one wrap catches every
  // call. Idempotent (the __vms flag), and it always calls through to the original.
  function installPauseHook() {
    if (typeof window.pauseAllMedia !== 'function') return false;
    if (window.pauseAllMedia.__vms) return true;
    var orig = window.pauseAllMedia;
    var wrapped = function () {
      dawnPauseAt = Date.now();
      var r = orig.apply(this, arguments);
      window.setTimeout(afterDawnPause, 60);
      return r;
    };
    wrapped.__vms = true;
    window.pauseAllMedia = wrapped;
    return true;
  }

  // Fallback re-assert, load-bearing ONLY when the hook could not be installed. With the
  // hook in place these listeners still fire but do nothing that matters.
  function markAction() {
    lastActionAt = Date.now();
    window.setTimeout(afterDawnPause, 60);
  }

  // ---- shared driver for the autoplay video sets ---------------------------
  // Both the main gallery videos (BEHAVIOUR 1) and the in-description page videos
  // (BEHAVIOUR 3) are managed identically: classify a manual pause, resume after a Dawn
  // pause, and play or pause by viewport visibility. The ONLY differences are the selector
  // and the visibility threshold, so the logic lives here once. It never touches a video's
  // attributes: a data-vms-page video is preload="none" with NO autoplay, so the observer
  // (created after window load) is what first starts it, exactly like a main gallery video;
  // a legacy video[autoplay] clip, if any, is started by the browser instead. Either way we
  // only keep them going and pause them when off screen.
  function manageVideoSet(videos, threshold) {
    if (!videos.length) return;
    if (typeof IntersectionObserver === 'undefined') return;

    // Per-video visibility, written by the observer and read by the re-assert path. A
    // WeakMap keeps it off the DOM and lets the nodes be collected. It stays empty until
    // startObserving() runs (after window load), so everything that reads it is a safe
    // no-op until then.
    var intersecting = new WeakMap();

    Array.prototype.forEach.call(videos, function (video) {
      // Classify a pause as the shopper's OWN only if the video is visible and the pause
      // is not part of a Dawn pauseAllMedia (dawnPauseAt) nor a tab-hide (hiddenAt). This
      // is what stops a lightbox open or a tab-hide from being misread as a manual pause.
      // Without the hook, dawnPauseAt never advances, so fall back to the action-timing gap.
      // The in-description clip's own onclick toggle produces a real pause this respects.
      video.addEventListener('pause', function () {
        if (!intersecting.get(video)) return;
        if (Date.now() - hiddenAt <= 200) return;
        var notDawn = hookInstalled
          ? Date.now() - dawnPauseAt > 200
          : Date.now() - lastActionAt > 500;
        if (notDawn) video.setAttribute('data-vms-user-paused', '');
      });
      // A play event clears the mark: the shopper pressed play, so auto-play may resume
      // managing this video again.
      video.addEventListener('play', function () {
        video.removeAttribute('data-vms-user-paused');
      });
    });

    // The resume, registered in the shared registry. The open-modal check keeps a video
    // paused behind the lightbox and lets it resume on close.
    registerResume(function () {
      if (anyModalOpen()) return;
      Array.prototype.forEach.call(videos, function (video) {
        if (intersecting.get(video) && !video.hasAttribute('data-vms-user-paused')) {
          safePlay(video);
        }
      });
    });

    // The observer drives play or pause by visibility. For the main videos it is also what
    // first starts playback, and therefore fetching, since they are preload="none"; per the
    // PAGE-LOAD-COMES-FIRST rule it is not created until the whole page has rendered: wait
    // for window load, or if the document is already complete, yield one idle tick so we
    // never race first paint. A data-vms-page video is preload="none" with no autoplay, so this
    // observer is what first starts it too; a legacy video[autoplay] clip, if any, is already
    // playing by then and the observer only keeps managing what the browser started.
    function startObserving() {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target;
            var visible = entry.isIntersecting;
            intersecting.set(video, visible);
            if (visible) {
              // Do not fight a shopper who paused this one on purpose.
              if (!video.hasAttribute('data-vms-user-paused')) safePlay(video);
            } else {
              video.pause();
            }
          });
        },
        { threshold: threshold }
      );
      Array.prototype.forEach.call(videos, function (video) {
        observer.observe(video);
      });
    }
    function deferStart() {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(startObserving);
      } else {
        window.setTimeout(startObserving, 0);
      }
    }
    if (document.readyState === 'complete') {
      deferStart();
    } else {
      window.addEventListener('load', deferStart, { once: true });
    }
  }

  // ---- BEHAVIOUR 1: main gallery videos ------------------------------------
  function initMainVideos() {
    manageVideoSet(document.querySelectorAll('video[data-vms-autoplay]'), 0.5);
  }

  // ---- BEHAVIOUR 3: in-description page videos -----------------------------
  // The clip the owner authored into the product description (33FD001 has one below the
  // description). It now carries data-vms-page with preload="none" and no autoplay, so the
  // observer is what first starts it after window load (see the BEHAVIOUR 3 header for why
  // the native autoplay attribute was dropped). The old video[autoplay] branch stays
  // selected so any future authored autoplay clip is still managed. Dawn's pauseAllMedia
  // killed the clip on any gallery action and nothing brought it back; now it is a managed
  // set like the gallery videos. iframes are never selected, so the lazy YouTube embed stays
  // exactly as authored (owner: "The youtube video can remain non-autoplay"). Threshold 0.25
  // because a wide description clip is often only partly in view.
  function initPageVideos() {
    manageVideoSet(
      document.querySelectorAll('video[data-vms-page], video[autoplay]:not([data-vms-autoplay]):not([data-vms-thumb])'),
      0.25
    );
  }

  // ---- BEHAVIOUR 2: desktop video thumbnails -------------------------------
  function initThumbVideos() {
    var thumbs = document.querySelectorAll('video[data-vms-thumb]');
    if (!thumbs.length) return;
    var pauseBtn = document.querySelector('.vms-thumb-pause');

    // Session memory of the shopper's pause choice. sessionStorage can throw in
    // locked-down privacy modes, so every access is guarded.
    function prefPaused() {
      try {
        return sessionStorage.getItem('vmsThumbPreviewPaused') === '1';
      } catch (e) {
        return false;
      }
    }
    function setPref(paused) {
      try {
        if (paused) {
          sessionStorage.setItem('vmsThumbPreviewPaused', '1');
        } else {
          sessionStorage.removeItem('vmsThumbPreviewPaused');
        }
      } catch (e) {
        // Ignore: the choice simply is not remembered across pages this session.
      }
    }

    // Only thumbnails whose rail is actually on screen. offsetParent is null when an
    // ancestor is display:none, which is exactly the phone case (the rail is hidden).
    function displayedThumbs() {
      return Array.prototype.filter.call(thumbs, function (video) {
        return video.offsetParent !== null;
      });
    }

    function playThumb(video) {
      var onPlaying = function () {
        video.classList.add('is-ready');
        video.removeEventListener('playing', onPlaying);
      };
      video.addEventListener('playing', onPlaying);
      // The whole point of this behaviour: the preview loops continuously.
      video.loop = true;
      safePlay(video);
    }

    function playAll() {
      displayedThumbs().forEach(playThumb);
    }
    function pauseAll() {
      Array.prototype.forEach.call(thumbs, function (video) {
        video.pause();
      });
    }

    function reflectButton(paused) {
      if (!pauseBtn) return;
      pauseBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      pauseBtn.textContent = paused ? 'Play previews' : 'Pause previews';
    }

    // The thumbnail resume, registered in the shared registry. It respects the session
    // pause choice but, per spec, does not check for an open modal.
    registerResume(function () {
      if (prefPaused()) return;
      playAll();
    });

    if (pauseBtn) {
      pauseBtn.addEventListener('click', function () {
        // aria-pressed reflects the CURRENT state, so a click flips it.
        var goPaused = pauseBtn.getAttribute('aria-pressed') !== 'true';
        if (goPaused) pauseAll();
        setPref(goPaused);
        reflectButton(goPaused);
        if (!goPaused) playAll();
      });
    }

    function run() {
      var paused = prefPaused();
      reflectButton(paused);
      // Honor a remembered pause: nothing plays until the shopper presses resume.
      if (paused) return;
      playAll();
    }

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run);
    } else {
      window.setTimeout(run, 1500);
    }
  }

  // ---- tab visibility -------------------------------------------------------
  function initVisibility() {
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        // Set hiddenAt BEFORE pausing so the per-video pause handlers see it and do not
        // mistake this tab-hide for a manual pause.
        hiddenAt = Date.now();
        var all = document.querySelectorAll('video[data-vms-autoplay], video[data-vms-thumb], video[data-vms-page], video[autoplay]:not([data-vms-autoplay]):not([data-vms-thumb])');
        Array.prototype.forEach.call(all, function (video) {
          video.pause();
        });
      } else {
        // Back in view: re-assert exactly like after a Dawn pause.
        afterDawnPause();
      }
    });
  }

  // ---- fallback action hooks (only when the global could not be wrapped) ----
  function installFallbackHooks() {
    document.querySelectorAll('[id^="GalleryViewer"]').forEach(function (viewer) {
      viewer.addEventListener('slideChanged', markAction);
    });
    document.querySelectorAll('[id^="GalleryThumbnails"]').forEach(function (thumbs) {
      thumbs.addEventListener('click', markAction);
    });
    // A variant change routes through setActiveMedia -> pauseAllMedia. pubsub.js loads
    // deferred, so subscribe may not exist yet; deferred scripts finish before
    // DOMContentLoaded, so wiring there is guaranteed to find it.
    function wireVariantChange() {
      if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return false;
      subscribe(PUB_SUB_EVENTS.variantChange, markAction);
      return true;
    }
    if (!wireVariantChange()) {
      document.addEventListener('DOMContentLoaded', wireVariantChange, { once: true });
    }
  }

  function boot() {
    // Reduced motion: touch nothing. Controls stay, so a shopper can still press play.
    if (REDUCED) return;
    // Run when ANY of the three managed sets is present: main gallery videos, thumbnail
    // previews, or an in-description page autoplay video (the last needs the script too).
    if (!document.querySelector('video[data-vms-autoplay], video[data-vms-thumb], video[data-vms-page], video[autoplay]:not([data-vms-autoplay]):not([data-vms-thumb])')) return;

    hookInstalled = installPauseHook();
    initMainVideos();
    initPageVideos();
    initVisibility();
    // The listeners below matter only without the hook; with it, the wrapper re-asserts.
    if (!hookInstalled) installFallbackHooks();

    // Thumbnails wait for full load, then idle time (inside initThumbVideos).
    if (document.readyState === 'complete') {
      initThumbVideos();
    } else {
      window.addEventListener('load', initThumbVideos, { once: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Optional debugging handle only; nothing depends on it.
  window.VMSGalleryVideo = {
    initMainVideos: initMainVideos,
    initPageVideos: initPageVideos,
    initThumbVideos: initThumbVideos
  };
})();
